import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#b87333';
  const lastUpdated = 'April 24, 2026';

  return (
    <LegalLayout app="Ensemble" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the Ensemble mobile application
                (the "App"). Ensemble is a multi-dish cooking orchestrator that schedules every
                step of every recipe backwards from a single finish time, so every dish lands
                on the table together.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use the App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                Ensemble is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local-First Storage:</strong> All recipes, meal sessions, timelines, and history are stored on your device using Apple's SwiftData framework.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party Tracking:</strong> We do not integrate any third-party analytics, tracking, or advertising SDKs.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>On-Device AI:</strong> Photo scanning and voice recognition run entirely on your device using Apple's Vision and Speech frameworks.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Recipe & Meal Data
              </h3>
              <p className="mb-4 leading-relaxed">
                Ensemble stores the recipes and meal sessions you create, which may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Recipe titles, servings, and source URLs (if you imported from a website)</li>
                <li>Recipe steps with durations, types (active or passive), and preferred cooking zones</li>
                <li>Meal sessions with target finish times, generated timelines, and cooking progress</li>
                <li>Completion timestamps, ratings, and history</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> All recipe and
                meal data is stored locally on your device and is never transmitted to any
                server owned or operated by us.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Photos (Photo Scan Feature)
              </h3>
              <p className="leading-relaxed">
                When you use the Photo Scan feature to import a recipe, the App accesses the
                photo you select (or capture with the camera). The image is processed entirely
                on your device using Apple's Vision framework to extract text. Photos are never
                uploaded, stored by us, or shared with third parties. The App does not retain
                the photo after the scan is complete — only the extracted recipe text.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Voice Input (Voice Commands Feature)
              </h3>
              <p className="leading-relaxed">
                When you enable voice commands in cooking mode, the App accesses the microphone
                and uses Apple's Speech framework to recognize commands like "done", "skip",
                "pause", and "resume". Audio is processed through Apple's Speech services and is
                never recorded, stored, or transmitted by us. The microphone is active only while
                voice commands are turned on.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                User Preferences
              </h3>
              <p className="mb-4 leading-relaxed">
                The App stores your preferences locally, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Default servings and default finish time</li>
                <li>Haptic feedback and keep-screen-awake toggles</li>
                <li>Whether you have completed the welcome tour</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage & Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored in Apple's SwiftData container on your device, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Remote Servers:</strong> Your recipes, timelines, and cooking history are never uploaded to external servers.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>iOS Sandbox:</strong> The App operates within iOS's security sandbox, preventing other apps from accessing your data.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                Ensemble does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Device location</li>
                <li>Contact information</li>
                <li>Photos beyond the single image you choose to scan (which is processed on-device)</li>
                <li>Voice audio recordings</li>
                <li>Browsing history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Network Connections
              </h2>
              <p className="mb-4 leading-relaxed">
                Ensemble connects to the internet only when you explicitly trigger one of the
                following actions:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Recipe Search:</strong> When you use the Search tab to look up a recipe, the App queries <a href="https://www.themealdb.com/" className="underline" style={{ color: accentColor }}>TheMealDB</a>, a free public recipe API. Your search query is sent over HTTPS. TheMealDB's privacy practices apply to that request.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>URL Import:</strong> When you paste a recipe URL, the App fetches that page directly from the URL you provided over HTTPS. We do not track which URLs you import.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Thumbnail Images:</strong> Recipe thumbnails from TheMealDB are loaded over HTTPS when shown in search results.</li>
              </ul>
              <p className="mt-4 leading-relaxed">
                No background network activity occurs. The App does not phone home.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Notifications
              </h2>
              <p className="leading-relaxed">
                Ensemble may ask for permission to send local notifications so it can alert you
                when a long passive cooking task (e.g. a simmer or bake) finishes while the App
                is in the background. All notifications are scheduled locally on your device.
                We do not send push notifications from our servers — in fact, we don't operate any.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                In-App Purchases
              </h2>
              <p className="leading-relaxed">
                Ensemble offers an optional <strong style={{ color: 'var(--text-primary)' }}>Ensemble Pro</strong>
                {' '}one-time purchase that unlocks photo scan, voice commands, and unlimited
                dishes per meal. All purchases are processed entirely by the Apple App Store. We do not process,
                store, or have access to your payment information. Purchase verification is handled
                through Apple's StoreKit framework.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="mb-4 leading-relaxed">
                Ensemble uses <strong style={{ color: 'var(--text-primary)' }}>Apple's native frameworks</strong>:
                SwiftUI, SwiftData, Vision (on-device OCR), Speech (on-device recognition),
                AVFoundation, UserNotifications, and StoreKit.
              </p>
              <p className="leading-relaxed">
                Ensemble uses one third-party service: <strong style={{ color: 'var(--text-primary)' }}>TheMealDB</strong>,
                a free public recipe database, queried only when you actively search for a recipe.
                We do not integrate any third-party analytics tools, crash reporters, or
                advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights & Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View:</strong> Access all your recipes, meals, and history within the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Remove individual recipes, meals, or all data at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Ensemble is not directed at children under the age of 13. We do not knowingly
                collect personal information from children. If you believe a child has provided
                us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/ensemble/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    Ensemble Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
